/*
 * Naturlabs storefront components (vanilla, no dependencies). Loaded with defer after nl-motion.js.
 * Configuration comes from the inline window.NL object printed by layout/theme.liquid:
 *   NL.routes, NL.strings, NL.settings, NL.moneyFormat
 *
 * Public API
 *   NL.cart.get() / add(items, opts) / addForm(form, opts) / change(lineKey, qty, opts) / update(updates, opts) / clear(opts) / refresh()
 *   NL.productKey(aliasOrProduct) / NL.productURL(aliasOrProduct)
 *   NL.wishlist.get() / has(handle) / add(handle) / remove(handle) / toggle(handle) / count() / clear()
 *   NL.toast(message, { variant: 'success' | 'error' | 'info', action: { href, label }, timeout })
 *   NL.formatMoney(cents, format?, options?) or NL.formatMoney(cents, { trimZeros: true })
 *   NL.emit(name, detail), NL.debounce(fn, ms)
 *
 * Events (CustomEvent on document unless noted, all bubble)
 *   cart:adding      { token, lines, source, opener, info, open }  right before an add request (optimistic UI)
 *   nl:cart:updated  { cart, source, sections, token }  theme listeners use this name: apps (Globe pickup points)
 *                    dispatch their own "cart:updated" without data on every cart fetch
 *   cart:updated     { cart, source, sections, token }  same detail, kept for third party listeners
 *   cart:item-added  { items, cart, source, token }
 *   cart:error       { message, source, token }
 *   cart:open        dispatch it to open the cart drawer   { opener }
 *   wishlist:changed { handle, added, handles, count }
 *   variant:change   { variant, productId, sectionId, form }
 *   dialog:open / dialog:close  { id }   (from the dialog host)
 *   accordion:toggle { open }            (from <accordion-item>)
 *   tabs:change      { index, tab, panel } (from <tabs-component>)
 *   gallery:change   { index, mediaId }   (from <media-gallery>)
 *   nl:toast         dispatch it to show a toast { message, variant, action, timeout }
 *
 * Elements: nl-dialog, cart-drawer, cart-count, predictive-search, wishlist-toggle, wishlist-count, quantity-input,
 *           menu-drawer, header-menu, accordion-item, product-form, sticky-atc, media-gallery, scroll-rail, tabs-component
 */
(() => {
  'use strict';

  const NL = (window.NL = window.NL || {});
  const routes = Object.assign(
    {
      root: '/',
      cart: '/cart',
      cartAdd: '/cart/add',
      cartChange: '/cart/change',
      cartUpdate: '/cart/update',
      search: '/search',
      predictiveSearch: '/search/suggest',
      recommendations: '/recommendations/products',
      wishlist: '',
    },
    NL.routes || {}
  );
  NL.routes = routes;
  const strings = NL.strings || {};
  const settings = Object.assign({ cartDrawer: true, cartUpsell: true, predictiveSearch: true, wishlist: true }, NL.settings || {});
  NL.settings = settings;
  NL.state = NL.state || { cart: null };

  /* ==========================================================================
     Helpers
     ========================================================================== */

  const $ = (selector, ctx = document) => ctx.querySelector(selector);
  const $$ = (selector, ctx = document) => Array.from(ctx.querySelectorAll(selector));
  const emit = (name, detail = {}, target = document) =>
    target.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  const debounce = (fn, wait = 200) => {
    let id;
    return (...args) => {
      window.clearTimeout(id);
      id = window.setTimeout(() => fn(...args), wait);
    };
  };
  const reduced = () =>
    NL.motion ? NL.motion.reduced : window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const fill = (template, vars = {}) =>
    String(template || '').replace(/%(\w+)%/g, (match, key) => (key in vars ? vars[key] : match));
  const plural = (forms, count) => {
    let rule = 'other';
    try {
      rule = new Intl.PluralRules(document.documentElement.lang || 'cs').select(count);
    } catch (e) {
      rule = count === 1 ? 'one' : 'other';
    }
    return fill((forms && (forms[rule] || forms.other)) || '', { count });
  };
  const isCartPage = () => settings.template === 'cart';
  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  /* Stable product identities are independent of the current localized handle. The Liquid payload
     supplies the current ProductDrop URL; this exact allowlist prevents stale or conflicting data
     from silently selecting another product. A missing URL never becomes a constructed route. */
  const PRODUCT_IDENTITIES = [
    ["nl-1201","10776006164827","54236916220251","00150010",["nl-1201","horcik-vitamin-b6","magnesium-vitamin-b6","magnesium-mit-vitamin-b6"]],
    ["nl-1202","10776006197595","54236916253019","00150020",["nl-1202","zinek-selen-med","zinc-selenium-copper","zink-selen-kupfer"]],
    ["nl-1206","10776006230363","54236916580699","00150060",["nl-1206","zelezo-folat","iron-folate","eisen-folat"]],
    ["nl-1207","10776006263131","54236916613467","00150070",["nl-1207","vapnik-vitamin-d3-k2","calcium-vitamin-d3-k2","calcium-mit-vitamin-d3-k2"]],
    ["nl-1208","10776006328667","54236917006683","10000150080",["nl-1208","jod-l-tyrosin","iodine-l-tyrosine","jod-und-l-tyrosin"]],
    ["nl-1213","10776006394203","54236920119643","00150130",["nl-1213","folat-quatrefolic","folate-quatrefolic","folat-mit-quatrefolic"]],
    ["nl-1214","10776006426971","54236920250715","00150140",["nl-1214","vitamin-b12-vitality"]],
    ["nl-1215","10776006459739","54236920283483","1015000150",["nl-1215","melatonin-booster"]],
    ["nl-1226","10776006492507","54236920447323","00150260",["nl-1226","vitamin-c"]],
    ["nl-1236","10776006820187","54236921430363","00150360",["nl-1236","msm"]],
    ["nl-1240","10776006984027","54236921889115","00150400",["nl-1240","vitamin-d3-k2"]],
    ["nl-1241","10776007016795","54236921921883","00150410",["nl-1241","kyselina-hyaluronova-vitamin-c","hyaluronic-acid-vitamin-c","hyaluronsaeure-vitamin-c"]],
    ["nl-1244","10776007049563","54236921954651","00150440",["nl-1244","horcik-magtein-vitaminy-b","magnesium-magtein-b-vitamins","magnesium-magtein-b-vitamine"]],
    ["nl-1246","10776007147867","54236922347867","00150460",["nl-1246","multivitamin-mineraly","multivitamin-minerals","multivitamin-mineralstoffe"]]
  ];
  const identityId = (value) => typeof value === 'number' || typeof value === 'string'
    ? String(value).replace(/^gid:\/\/shopify\/Product\//, '') : '';
  const identityAlias = (value) => typeof value === 'string' ? value.trim().toLowerCase() : '';
  const knownIdentities = new Map(PRODUCT_IDENTITIES.map(([key, id, variant, sku, aliases]) =>
    [key, { key, id, variant, sku, aliases }]));
  const knownAliases = new Map();
  knownIdentities.forEach((entry) => entry.aliases.forEach((alias) => knownAliases.set(alias, entry.key)));
  const productIdentityRegistry = () => {
    const payload = NL.productIdentities;
    const result = new Map();
    if (!payload || payload.schema !== 1 || !Array.isArray(payload.products)) return result;
    const blocked = new Set();
    const seenIds = new Map();
    payload.products.forEach((row) => {
      if (!row || typeof row !== 'object') return;
      const known = knownIdentities.get(row.key);
      const id = identityId(row.productId);
      if (seenIds.has(id)) {
        blocked.add(row.key);
        blocked.add(seenIds.get(id));
      }
      seenIds.set(id, row.key);
      if (result.has(row.key)) blocked.add(row.key);
      const aliases = Array.isArray(row.aliases) ? row.aliases : [];
      aliases.forEach((alias) => {
        const owner = knownAliases.get(identityAlias(alias));
        if (owner && owner !== row.key) {
          blocked.add(owner);
          blocked.add(row.key);
        }
      });
      if (!known || id !== known.id || identityId(row.variantId) !== known.variant || row.sku !== known.sku ||
          aliases.length !== known.aliases.length || new Set(aliases).size !== aliases.length ||
          !known.aliases.every((alias) => aliases.includes(alias))) {
        blocked.add(row.key);
        return;
      }
      result.set(row.key, row);
    });
    blocked.forEach((key) => result.delete(key));
    return result;
  };
  const productIdentity = (input) => {
    const registry = productIdentityRegistry();
    if (typeof input === 'string') return registry.get(knownAliases.get(identityAlias(input))) || null;
    if (!input || typeof input !== 'object') return null;
    const aliases = ['key', 'productKey', 'handle'].filter((field) => input[field] !== undefined && input[field] !== null);
    const ids = ['productId', 'product_id', 'id'].filter((field) => input[field] !== undefined && input[field] !== null);
    const keys = aliases.map((field) => knownAliases.get(identityAlias(input[field])));
    if (keys.some((key) => !key) || new Set(keys).size > 1) return null;
    const providedIds = ids.map((field) => identityId(input[field]));
    if (providedIds.some((id) => !/^\d+$/.test(id)) || new Set(providedIds).size > 1) return null;
    const key = keys[0] || (providedIds.length && PRODUCT_IDENTITIES.find((row) => row[1] === providedIds[0]) || [])[0];
    const entry = registry.get(key);
    if (!entry || (providedIds.length && String(entry.productId) !== providedIds[0])) return null;
    return entry;
  };
  NL.productKey = (input) => {
    const entry = productIdentity(input);
    return entry ? entry.key : null;
  };
  NL.productURL = (input) => {
    const entry = productIdentity(input);
    if (!entry || entry.verified !== true || !entry.aliases.includes(entry.handle) ||
        typeof entry.url !== 'string' || !entry.url.startsWith('/') || entry.url.startsWith('//') ||
        /[\\\s]/.test(entry.url)) return null;
    try {
      const url = new URL(entry.url, window.location.origin);
      return url.origin === window.location.origin && !url.hash ? entry.url : null;
    } catch (error) {
      return null;
    }
  };
  const identityList = (list) => [...new Set(list.map((value) => NL.productKey(value) || value))];

  const ICONS = {
    check:
      '<svg class="nl-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>',
    alert:
      '<svg class="nl-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>',
    info:
      '<svg class="nl-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>',
    close:
      '<svg class="nl-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>',
  };

  /* Amounts are already in the active cart currency. Formatting never converts their value. */
  const activeCurrency = () => {
    const cartCurrency = NL.state && NL.state.cart && NL.state.cart.currency;
    return String((cartCurrency && (cartCurrency.iso_code || cartCurrency)) || settings.currency ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || NL.moneyFormatCurrency || '').toUpperCase();
  };
  const formatMoney = (cents, format, options = {}) => {
    if (format && typeof format === 'object') {
      options = format;
      format = undefined;
    }
    const value = typeof cents === 'string' ? Number(cents.replace(/[^\d-]/g, '')) : Number(cents) || 0;
    const currency = activeCurrency();
    const locale = settings.locale || document.documentElement.lang || 'cs';
    const trimZeros = options.trimZeros === true && value % 100 === 0;
    let template = format === undefined ? NL.moneyFormat : format;
    // shop.money_format belongs to shop.currency, even when cart prices are presentment EUR.
    if (typeof template !== 'string' || currency !== NL.moneyFormatCurrency) {
      const intlOptions = { minimumFractionDigits: trimZeros ? 0 : 2, maximumFractionDigits: 2 };
      if (/^[A-Z]{3}$/.test(currency)) Object.assign(intlOptions, { style: 'currency', currency });
      return new Intl.NumberFormat(locale, intlOptions).format(value / 100);
    }
    if (trimZeros) {
      template = template.replace(/\{\{\s*amount(_with_comma_separator|_with_space_separator|_with_apostrophe_separator)?\s*\}\}/, (all, kind) => {
        if (kind === '_with_comma_separator') return '{{amount_no_decimals_with_comma_separator}}';
        if (kind === '_with_space_separator') return '{{amount_no_decimals_with_space_separator}}';
        if (kind === '_with_apostrophe_separator') return '{{amount_no_decimals_with_apostrophe_separator}}';
        return '{{amount_no_decimals}}';
      });
    }
    const group = (amount, decimals, thousands, decimal) => {
      const [int, frac] = (amount / 100).toFixed(decimals).split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${grouped}${decimal}${frac}` : grouped;
    };
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const match = template.match(pattern);
    if (!match) return template;
    const map = {
      amount: () => group(value, 2, ',', '.'),
      amount_no_decimals: () => group(value, 0, ',', '.'),
      amount_with_comma_separator: () => group(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => group(value, 0, '.', ','),
      amount_with_space_separator: () => group(value, 2, ' ', ','),
      amount_no_decimals_with_space_separator: () => group(value, 0, ' ', ','),
      amount_with_apostrophe_separator: () => group(value, 2, "'", '.'),
      amount_no_decimals_with_apostrophe_separator: () => group(value, 0, "'", '.'),
    };
    return template.replace(pattern, (map[match[1]] || map.amount)());
  };
  NL.activeCurrency = activeCurrency;

  NL.emit = emit;
  NL.debounce = debounce;
  NL.formatMoney = formatMoney;

  /* ==========================================================================
     Toasts
     ========================================================================== */

  NL.toast = (message, options = {}) => {
    const region = document.getElementById('NlToastRegion');
    if (!region || !message) return null;
    const { variant = 'success', action = null, timeout = 4200 } = options;

    const toast = document.createElement('div');
    toast.className = `nl-toast nl-toast--${variant}`;
    if (variant === 'error') toast.setAttribute('role', 'alert');

    const icon = document.createElement('span');
    icon.className = 'nl-toast__icon';
    icon.innerHTML = ICONS[variant === 'error' ? 'alert' : variant === 'info' ? 'info' : 'check'];

    const text = document.createElement('p');
    text.className = 'nl-toast__text';
    text.textContent = message;
    if (action && action.href && action.label) {
      const link = document.createElement('a');
      link.className = 'nl-toast__action';
      link.href = action.href;
      link.textContent = action.label;
      text.append(' ', link);
    }

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'nl-toast__close';
    close.setAttribute('aria-label', strings.close || 'Zavřít');
    close.innerHTML = ICONS.close;

    toast.append(icon, text, close);
    region.append(toast);
    while (region.children.length > 3) region.firstElementChild.remove();

    requestAnimationFrame(() => requestAnimationFrame(() => toast.classList.add('is-visible')));

    let timer;
    const remove = () => {
      window.clearTimeout(timer);
      toast.classList.add('is-leaving');
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), reduced() ? 0 : 420);
    };
    const start = () => {
      window.clearTimeout(timer);
      if (timeout > 0) timer = window.setTimeout(remove, timeout);
    };
    close.addEventListener('click', remove);
    toast.addEventListener('mouseenter', () => window.clearTimeout(timer));
    toast.addEventListener('mouseleave', start);
    toast.addEventListener('focusin', () => window.clearTimeout(timer));
    toast.addEventListener('focusout', start);
    start();
    return toast;
  };

  document.addEventListener('nl:toast', (event) => {
    const d = event.detail || {};
    NL.toast(d.message, d);
  });

  /* ==========================================================================
     Cart API (AJAX Cart API + Section Rendering API)
     Any element with [data-cart-section="<section id>"] is re-rendered after every cart change.
     ========================================================================== */

  let cartQueue = Promise.resolve();
  const queued = (task) => {
    const run = cartQueue.then(() => {
      const relay = window.NLLanguageCartRelay;
      return relay && typeof relay.runMutation === 'function' ? relay.runMutation(task) : task();
    });
    cartQueue = run.catch(() => {});
    return run;
  };

  const request = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...(options.headers || {}) },
    });
    let data = null;
    try {
      data = await response.json();
    } catch (e) {
      data = null;
    }
    if (!response.ok || (data && typeof data.status === 'number' && data.status >= 400)) {
      const raw = data && (data.description || data.message || data.errors);
      const message = typeof raw === 'string' && raw ? raw : strings.errorGeneric || 'Error';
      const error = new Error(message);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data;
  };

  const cartSectionIds = () =>
    [...new Set($$('[data-cart-section]').map((el) => el.dataset.cartSection).filter(Boolean))].slice(0, 5);

  /* sections/nl-cart-json.liquid renders {{ cart | json }}: bundled with the add request (Section Rendering API), it
     replaces the second, sequential GET /cart.js after every add (one network round trip less, 0.1 to 2 s). */
  const CART_JSON_SECTION = 'nl-cart-json';
  const readCartJson = (sections) => {
    const html = sections && sections[CART_JSON_SECTION];
    if (typeof html !== 'string') return null;
    const match = html.match(/<script[^>]*data-cart-json[^>]*>([\s\S]*?)<\/script>/);
    if (!match) return null;
    try {
      const cart = JSON.parse(match[1]);
      return cart && typeof cart.item_count === 'number' && Array.isArray(cart.items) ? cart : null;
    } catch (e) {
      return null;
    }
  };

  const applySections = (sections) => {
    if (!sections) return;
    const active = document.activeElement;
    const activeId = active && active.id;
    Object.entries(sections).forEach(([id, html]) => {
      if (typeof html !== 'string' || id === CART_JSON_SECTION) return;
      const doc = parseHTML(html);
      const selector = `[data-cart-section="${CSS.escape(id)}"]`;
      const fresh = doc.querySelector(selector);
      if (!fresh) return;
      $$(selector).forEach((target) => {
        target.replaceChildren(...Array.from(fresh.cloneNode(true).childNodes));
        Array.from(fresh.attributes).forEach((attr) => {
          if (attr.name !== 'data-cart-section') target.setAttribute(attr.name, attr.value);
        });
      });
    });
    if (activeId && document.activeElement !== active) {
      const again = document.getElementById(activeId);
      if (again) again.focus({ preventScroll: true });
    }
  };

  const withSections = (body, withCart = false) => {
    const ids = cartSectionIds();
    if (withCart) {
      if (ids.length > 4) ids.length = 4;
      ids.push(CART_JSON_SECTION);
    }
    if (!ids.length) return body;
    if (body instanceof FormData) {
      body.append('sections', ids.join(','));
      body.append('sections_url', window.location.pathname);
      return body;
    }
    return { ...body, sections: ids.join(','), sections_url: window.location.pathname };
  };

  const announceCart = (cart, source, sections, token) => {
    NL.state.cart = cart;
    const detail = { cart, source, sections, token };
    emit('nl:cart:updated', detail);
    emit('cart:updated', detail);
  };

  /* Cart feedback is negotiable before the optimistic drawer opens. A mounted offer controller may
     hold that first feedback, then claim cart:feedback only after fresh server sections arrive. If it
     cannot show an offer, ordinary drawer feedback remains the fallback. Other adds stay optimistic. */
  let addSeq = 0;
  const drawerTarget = (options) => {
    if (options.open === false || !settings.cartDrawer || isCartPage()) return null;
    return $('cart-drawer');
  };
  const startAdd = (lines, options) => {
    addSeq += 1;
    const token = addSeq;
    const drawer = drawerTarget(options);
    const feedback = { token, lines, source: options.source || 'add', opener: options.opener || null, open: Boolean(drawer) };
    const beforeDrawer = new CustomEvent('cart:before-drawer', { detail: feedback, bubbles: true, cancelable: true });
    if (drawer) document.dispatchEvent(beforeDrawer);
    const feedbackDeferred = Boolean(drawer && beforeDrawer.defaultPrevented);
    emit('cart:adding', {
      ...feedback,
      info: options.optimistic || null,
      open: Boolean(drawer) && !feedbackDeferred,
      feedbackDeferred,
    });
    let opened = false;
    if (drawer && !feedbackDeferred && typeof drawer.open === 'function') {
      if (!drawer.isOpen) drawer.open(options.opener);
      opened = Boolean(drawer.isOpen);
    }
    return { ...options, token, openedEarly: opened, feedbackDeferred };
  };
  const linesFromForm = (form) => {
    const data = new FormData(form);
    const id = data.get('id');
    if (id) return [{ id: Number(id), quantity: Number(data.get('quantity') || 1) }];
    const lines = [];
    data.forEach((value, key) => {
      const m = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      if (!m) return;
      const line = (lines[Number(m[1])] = lines[Number(m[1])] || { id: 0, quantity: 1 });
      line[m[2]] = Number(value);
    });
    return lines.filter((line) => line && line.id);
  };

  const afterAdd = async (response, options = {}) => {
    const items = response && Array.isArray(response.items) ? response.items : [response];
    const sections = response && response.sections;
    // Fallback when the cart JSON section did not come back: the old sequential read
    const cart = readCartJson(sections) || (await request(`${routes.cart}.js`));
    applySections(sections);
    announceCart(cart, options.source || 'add', sections, options.token);
    emit('cart:item-added', { items, cart, source: options.source || 'add', token: options.token });
    const feedback = new CustomEvent('cart:feedback', {
      detail: { items, cart, source: options.source || 'add', token: options.token, opener: options.opener || null, feedbackDeferred: options.feedbackDeferred === true },
      bubbles: true,
      cancelable: true,
    });
    if (options.open !== false) document.dispatchEvent(feedback);

    if (options.open !== false && !feedback.defaultPrevented) {
      const drawer = $('cart-drawer');
      if (drawer && settings.cartDrawer && !isCartPage()) {
        // Opened at the tap already: when the visitor closed it meanwhile, do not push it back in
        if (!options.openedEarly) drawer.open(options.opener);
      } else {
        const title = items[0] && items[0].product_title;
        NL.toast(title ? fill(strings.cartAddedNamed, { title }) : strings.cartAdded, {
          action: isCartPage() ? null : { href: routes.cart, label: strings.viewCart },
        });
      }
    }
    return { items, cart };
  };

  const fail = (error, options = {}) => {
    emit('cart:error', { message: error.message, source: options.source, token: options.token });
    throw error;
  };

  NL.cart = {
    whenIdle: () => cartQueue,
    get: () => request(`${routes.cart}.js`),

    add: (items, options = {}) => {
      const list = (Array.isArray(items) ? items : [items]).map((item) => {
        const line = { id: Number(item.id), quantity: Number(item.quantity || 1) };
        if (item.properties) line.properties = item.properties;
        if (item.selling_plan) line.selling_plan = item.selling_plan;
        return line;
      });
      const opts = startAdd(list, options);
      return queued(async () => {
        const response = await request(`${routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(withSections({ items: list }, true)),
        });
        return afterAdd(response, opts);
      }).catch((error) => fail(error, opts));
    },

    addForm: (form, options = {}) => {
      const opts = startAdd(linesFromForm(form), options);
      const body = new FormData(form);
      return queued(async () => {
        const response = await request(`${routes.cartAdd}.js`, {
          method: 'POST',
          body: withSections(body, true),
        });
        return afterAdd(response, opts);
      }).catch((error) => fail(error, opts));
    },

    change: (lineKey, quantity, options = {}) =>
      queued(async () => {
        const cart = await request(`${routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(withSections({ id: String(lineKey), quantity: Number(quantity) })),
        });
        applySections(cart.sections);
        announceCart(cart, options.source || 'change', cart.sections);
        return cart;
      }).catch((error) => fail(error, options)),

    update: (updates, options = {}) =>
      queued(async () => {
        const payload = withSections({ ...(options.note !== undefined ? { note: options.note } : {}), updates });
        const cart = await request(`${routes.cartUpdate}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        applySections(cart.sections);
        announceCart(cart, options.source || 'update', cart.sections);
        return cart;
      }).catch((error) => fail(error, options)),

    // Empties the cart (/cart/clear.js with the cart sections, owner 16. 10. 2026 „vyprázdnit košík“). Resolves with
    // the cart as it was before (options.before when the caller already holds a fresh copy), so the caller can offer
    // an undo with the same variants, quantities, properties and selling plans.
    clear: (options = {}) =>
      queued(async () => {
        const before = options.before || (await request(`${routes.cart}.js`));
        const cart = await request(`${routes.cartClear || `${routes.cart}/clear`}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(withSections({})),
        });
        applySections(cart.sections);
        announceCart(cart, options.source || 'clear', cart.sections);
        return { cart, before };
      }).catch((error) => fail(error, options)),

    // One request: the sections and the cart JSON section together (before: /cart.js, then the sections)
    refresh: (source = 'refresh') =>
      queued(async () => {
        const ids = cartSectionIds().slice(0, 4);
        ids.push(CART_JSON_SECTION);
        let sections = null;
        try {
          const response = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`, {
            credentials: 'same-origin',
          });
          if (response.ok) sections = await response.json();
        } catch (e) {
          sections = null;
        }
        const cart = readCartJson(sections) || (await request(`${routes.cart}.js`));
        applySections(sections);
        announceCart(cart, source, sections);
        return cart;
      }),
  };

  // Cart restored from bfcache (back button): sync counts and drawer
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) NL.cart.refresh('pageshow').catch(() => {});
  });

  /* ==========================================================================
     Wishlist (localStorage, stable product keys; unknown saved values are retained)
     ========================================================================== */

  const WISHLIST_KEY = 'nl:wishlist:v1';
  const readWishlist = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(value) ? identityList(value.filter((h) => typeof h === 'string' && h)) : [];
    } catch (e) {
      return [];
    }
  };
  const writeWishlist = (list) => {
    try {
      let known = 0;
      const retained = list.filter((value) => !NL.productKey(value) || ++known <= 100);
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(retained));
    } catch (e) {
      /* storage blocked: keep working in memory for this page */
    }
  };
  const wishlistCount = (list) => list.filter((value) => NL.productKey(value)).length;
  const wishlistChanged = (handle, added, list) =>
    emit('wishlist:changed', { handle, added, handles: list, count: wishlistCount(list) });

  NL.wishlist = {
    key: WISHLIST_KEY,
    get: readWishlist,
    has: (handle) => { const key = NL.productKey(handle); return Boolean(key && readWishlist().includes(key)); },
    count: () => wishlistCount(readWishlist()),
    add(handle) {
      const list = readWishlist();
      handle = NL.productKey(handle);
      if (!handle || list.includes(handle)) return;
      list.unshift(handle);
      writeWishlist(list);
      wishlistChanged(handle, true, list);
    },
    remove(handle) {
      handle = NL.productKey(handle);
      if (!handle) return;
      const list = readWishlist().filter((h) => h !== handle);
      writeWishlist(list);
      wishlistChanged(handle, false, list);
    },
    toggle(handle) {
      handle = NL.productKey(handle);
      if (!handle) return false;
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      }
      this.add(handle);
      return true;
    },
    clear() {
      writeWishlist([]);
      wishlistChanged(null, false, []);
    },
  };

  window.addEventListener('storage', (event) => {
    if (event.key !== WISHLIST_KEY) return;
    const list = readWishlist();
    emit('wishlist:changed', { handle: null, added: null, handles: list, count: wishlistCount(list), external: true });
  });

  /* ==========================================================================
     <nl-dialog>: base for modal hosts wrapping a native <dialog>
     Openers: [data-dialog-open="HostId"], closers: [data-dialog-close] inside the host.
     ========================================================================== */

  const DIALOG_HOSTS = 'nl-dialog, cart-drawer, menu-drawer';

  class NLDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector(':scope > dialog') || this.querySelector('dialog');
      if (!this.dialog || this._dialogBound) return;
      this._dialogBound = true;
      this.dialog.addEventListener('click', (event) => this.onBackdropClick(event));
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('close', () => this.onClosed());
    }

    get isOpen() {
      return Boolean(this.dialog && this.dialog.open);
    }

    open(opener) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener || document.activeElement;
      $$('.nl-dialog[open]').forEach((other) => {
        const host = other.closest(DIALOG_HOSTS);
        if (host && host !== this && typeof host.close === 'function') host.close(true);
      });
      // Scroll lock by a class (nl-components.css) instead of html:has(.nl-dialog[open]): the :has() rule made every
      // open and close restyle the whole document (150 to 300 ms on phones)
      document.documentElement.classList.add('nl-dialog-open');
      this.dialog.showModal();
      this.syncOpeners(true);
      if (typeof this.onOpen === 'function') this.onOpen();
      const focusTarget = this.dialog.querySelector('[autofocus], [data-autofocus]');
      if (focusTarget) requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
      emit('dialog:open', { id: this.id }, this);
    }

    close(silent) {
      if (!this.dialog || !this.dialog.open) return;
      if (silent) this.opener = null;
      this.dialog.close();
    }

    toggle(opener) {
      if (this.isOpen) this.close();
      else this.open(opener);
    }

    onClosed() {
      if (!document.querySelector('.nl-dialog[open]')) document.documentElement.classList.remove('nl-dialog-open');
      this.syncOpeners(false);
      const opener = this.opener;
      this.opener = null;
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
      if (typeof this.onClose === 'function') this.onClose();
      emit('dialog:close', { id: this.id }, this);
    }

    syncOpeners(state) {
      if (!this.id) return;
      $$(`[aria-controls="${CSS.escape(this.id)}"]`).forEach((el) => el.setAttribute('aria-expanded', String(state)));
    }

    onBackdropClick(event) {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside || this.dialog.hasAttribute('data-close-on-surface')) this.close();
    }
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      const host = document.getElementById(opener.dataset.dialogOpen);
      if (host && typeof host.open === 'function') {
        event.preventDefault();
        host.open(opener);
      }
      return;
    }
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      const host = closer.closest(DIALOG_HOSTS);
      if (host && typeof host.close === 'function') {
        event.preventDefault();
        host.close();
      }
    }
  });

  // Search shortcuts: "/" outside form fields, Ctrl or Cmd + K anywhere
  document.addEventListener('keydown', (event) => {
    const search = document.getElementById('SearchOverlay');
    if (!search || typeof search.open !== 'function' || search.isOpen) return;
    const typing = event.target.closest && event.target.closest('input, textarea, select, [contenteditable="true"]');
    const slash = event.key === '/' && !typing && !event.metaKey && !event.ctrlKey && !event.altKey;
    const cmdK = (event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K');
    if (slash || cmdK) {
      event.preventDefault();
      search.open();
    }
  });

  define('nl-dialog', NLDialog);

  /* ==========================================================================
     <cart-drawer>
     ========================================================================== */

  class CartDrawer extends NLDialog {
    connectedCallback() {
      super.connectedCallback();
      if (this._cartBound) return;
      this._cartBound = true;
      this.upsellCache = new Map();
      this.pending = new Map();

      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-cart-remove]');
        if (!remove) return;
        event.preventDefault();
        this.updateLine(remove.dataset.lineKey, 0, remove);
      });

      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-line-quantity]');
        if (!input) return;
        const key = input.dataset.lineKey;
        window.clearTimeout(this.pending.get(key));
        this.pending.set(
          key,
          window.setTimeout(() => this.updateLine(key, Math.max(0, parseInt(input.value, 10) || 0), input), 320)
        );
      });

      document.addEventListener('cart:open', (event) => this.open(event.detail && event.detail.opener));
      document.addEventListener('nl:cart:updated', () => {
        if (this.isOpen) this.loadUpsell();
      });
      document.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-cart-drawer-toggle]');
        if (!toggle || isCartPage() || !settings.cartDrawer) return;
        event.preventDefault();
        this.open(toggle);
      });
    }

    onOpen() {
      this.loadUpsell();
    }

    async updateLine(key, quantity, source) {
      // Cart lane hook: assets/nl-w2-cart.js (drawer owner) takes over busy state, inline errors and focus restore
      if (typeof this.lineUpdateHook === 'function') return this.lineUpdateHook(key, quantity, source);
      // The remove button and the quantity input carry data-line-key too: dim the whole line, not the control
      const line = source && (source.closest('.nl-cart-line') || source.closest('li[data-line-key]'));
      if (line) {
        line.setAttribute('aria-busy', 'true');
        line.classList.add('is-loading');
      }
      try {
        await NL.cart.change(key, quantity, { source: 'cart-drawer' });
        if (!this.contains(document.activeElement)) {
          const fallback = this.querySelector('[data-cart-drawer-focus]') || this.querySelector('.nl-close');
          if (fallback) fallback.focus({ preventScroll: true });
        }
      } catch (error) {
        NL.toast(error.message || strings.cartError, { variant: 'error' });
        NL.cart.refresh('cart-drawer').catch(() => {});
      }
    }

    async loadUpsell() {
      const box = this.querySelector('[data-cart-upsell]');
      if (!box || !settings.cartUpsell) return;
      const productId = box.dataset.productId;
      if (!productId) return;
      const limit = box.dataset.limit || 4;
      const render = (markup) => {
        const target = box.querySelector('[data-cart-upsell-target]') || box;
        target.innerHTML = markup;
        box.hidden = !markup;
        if (NL.motion) NL.motion.reveal(box);
      };
      if (this.upsellCache.has(productId)) {
        render(this.upsellCache.get(productId));
        return;
      }
      if (box.dataset.loading) return;
      box.dataset.loading = '1';
      // W2 CRO hook: the box may name the section that renders the recommendations (carousel: nl-w2-cart-upsell)
      const section = box.dataset.upsellSection || 'nl-cart-upsell';
      try {
        let markup = await this.fetchUpsell(productId, limit, 'complementary', section);
        if (!markup) markup = await this.fetchUpsell(productId, limit, 'related', section);
        this.upsellCache.set(productId, markup);
        render(markup);
      } catch (error) {
        box.hidden = true;
      } finally {
        delete box.dataset.loading;
      }
    }

    async fetchUpsell(productId, limit, intent, section = 'nl-cart-upsell') {
      const params = new URLSearchParams({ section_id: section, product_id: productId, limit, intent });
      const response = await fetch(`${routes.recommendations}?${params}`, { credentials: 'same-origin' });
      if (!response.ok) return '';
      const doc = parseHTML(await response.text());
      const content = doc.querySelector('[data-upsell-content]');
      return content && content.querySelector('[data-upsell-item]') ? content.innerHTML.trim() : '';
    }
  }

  define('cart-drawer', CartDrawer);

  /* ==========================================================================
     <cart-count data-count="2">2</cart-count>
     ========================================================================== */

  class CartCount extends HTMLElement {
    connectedCallback() {
      // Theme event only, always with the cart: no extra GET /cart.js (apps fire a bare "cart:updated" per fetch)
      this.onUpdate = (event) => {
        const cart = (event.detail && event.detail.cart) || NL.state.cart;
        if (cart && typeof cart.item_count === 'number') this.render(cart.item_count);
      };
      document.addEventListener('nl:cart:updated', this.onUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener('nl:cart:updated', this.onUpdate);
    }

    render(count) {
      const previous = Number(this.dataset.count || 0);
      this.dataset.count = String(count);
      this.textContent = count > 99 ? '99+' : String(count);
      this.hidden = count === 0;
      const link = this.closest('a, button');
      const label = link && link.querySelector('[data-cart-count-label]');
      if (label) label.textContent = plural(strings.cartCount, count);
      if (count > previous && !reduced()) {
        this.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.35)' }, { transform: 'scale(1)' }], {
          duration: 420,
          easing: 'cubic-bezier(.34,1.4,.64,1)',
        });
      }
    }
  }

  define('cart-count', CartCount);

  /* ==========================================================================
     <quantity-input>: [data-qty-step="-1|1"] buttons around an input[type=number]
     ========================================================================== */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input || this._bound) return;
      this._bound = true;
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-qty-step]');
        if (!button || button.disabled) return;
        event.preventDefault();
        this.step(Number(button.dataset.qtyStep));
      });
      this.input.addEventListener('input', () => this.syncButtons());
      this.input.addEventListener('change', () => {
        this.clamp();
        this.syncButtons();
      });
      this.input.addEventListener('focus', () => this.input.select());
      this.syncButtons();
    }

    get min() {
      return this.input.min === '' ? 0 : Number(this.input.min);
    }

    get max() {
      return this.input.max === '' ? Infinity : Number(this.input.max);
    }

    get stepSize() {
      return Number(this.input.step) || 1;
    }

    clamp() {
      const value = Number(this.input.value);
      if (Number.isNaN(value)) this.input.value = String(Math.max(this.min, 1));
      else if (value < this.min) this.input.value = String(this.min);
      else if (value > this.max) this.input.value = String(this.max);
    }

    step(direction) {
      const previous = Number(this.input.value) || 0;
      const next = Math.min(this.max, Math.max(this.min, previous + direction * this.stepSize));
      if (next === previous) return;
      this.input.value = String(next);
      this.syncButtons();
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncButtons() {
      const value = Number(this.input.value) || 0;
      const minus = this.querySelector('[data-qty-step="-1"]');
      const plus = this.querySelector('[data-qty-step="1"]');
      if (minus) minus.disabled = value <= this.min;
      if (plus) plus.disabled = value >= this.max;
    }
  }

  define('quantity-input', QuantityInput);

  /* ==========================================================================
     <predictive-search>: combobox over the Predictive Search API (section predictive-search)
     ========================================================================== */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      if (!this.input || this._bound) return;
      this._bound = true;
      this.cache = new Map();
      this.options = [];
      this.activeIndex = -1;
      this.enabled = settings.predictiveSearch !== false && Boolean(this.results);

      this.input.addEventListener('input', debounce(() => this.onInput(), 220));
      this.input.addEventListener('input', () => this.toggleAttribute('data-has-term', this.term.length > 0));
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      if (this.input.form) {
        this.input.form.addEventListener('reset', () => {
          window.setTimeout(() => {
            this.clear();
            this.toggleAttribute('data-has-term', false);
            this.input.focus();
          }, 0);
        });
      }
    }

    get term() {
      return this.input.value.trim();
    }

    async onInput() {
      const term = this.term;
      if (!this.enabled) return;
      if (term.length < 2) {
        this.clear();
        return;
      }
      if (this.cache.has(term)) {
        this.render(this.cache.get(term), term);
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setAttribute('aria-busy', 'true');
      try {
        const params = new URLSearchParams({
          q: term,
          'resources[type]': 'product,collection,page,article',
          'resources[limit]': '6',
          'resources[limit_scope]': 'each',
          'resources[options][prefix]': 'last',
          section_id: 'predictive-search',
        });
        const response = await fetch(`${routes.predictiveSearch}?${params}`, {
          signal: this.controller.signal,
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error(String(response.status));
        const doc = parseHTML(await response.text());
        const content = doc.querySelector('[data-predictive-content]');
        const markup = content ? content.innerHTML : '';
        this.cache.set(term, markup);
        this.render(markup, term);
      } catch (error) {
        if (error.name !== 'AbortError') this.clear();
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    render(markup, term) {
      if (term !== this.term || !this.results) return;
      this.results.innerHTML = markup;
      this.results.hidden = !markup;
      this.input.setAttribute('aria-expanded', String(Boolean(markup)));
      this.options = $$('[role="option"]', this.results);
      this.options.forEach((option, index) => {
        if (!option.id) option.id = `${this.id || 'PredictiveSearch'}-option-${index}`;
        option.setAttribute('aria-selected', 'false');
      });
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      const countEl = this.results.querySelector('[data-results-count]');
      const count = countEl ? Number(countEl.dataset.resultsCount) : this.options.length;
      if (this.status) this.status.textContent = plural(strings.searchResults, count);
      if (NL.motion) NL.motion.reveal(this.results);
    }

    clear() {
      if (this.results) {
        this.results.innerHTML = '';
        this.results.hidden = true;
      }
      this.options = [];
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      if (this.status) this.status.textContent = '';
    }

    setActive(index) {
      if (!this.options.length) return;
      const count = this.options.length;
      this.activeIndex = (index + count) % count;
      this.options.forEach((option, i) => option.setAttribute('aria-selected', String(i === this.activeIndex)));
      const active = this.options[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }

    onKeydown(event) {
      // fixes lane 2026-10-03: inside the search overlay the first Escape closes the overlay (the native clearing of
      // a filled type=search field used to swallow it). The typed term stays for the next opening.
      if (event.key === 'Escape') {
        const host = this.closest('nl-dialog');
        if (host && host.isOpen && typeof host.close === 'function') {
          event.preventDefault();
          event.stopPropagation();
          host.close();
          return;
        }
      }
      if (event.key === 'Escape' && this.term) {
        event.preventDefault();
        event.stopPropagation();
        this.input.value = '';
        this.toggleAttribute('data-has-term', false);
        this.clear();
        return;
      }
      if (!this.options.length) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.setActive(this.activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.setActive(this.activeIndex - 1);
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        const option = this.options[this.activeIndex];
        const link = option.matches('a[href]') ? option : option.querySelector('a[href]');
        if (link) {
          event.preventDefault();
          link.click();
        }
      }
    }
  }

  define('predictive-search', PredictiveSearch);

  /* ==========================================================================
     <wishlist-toggle data-handle="..."><button aria-pressed="false">...</button></wishlist-toggle>
     <wishlist-count>
     ========================================================================== */

  class WishlistToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      if (!settings.wishlist) {
        this.hidden = true;
        return;
      }
      this.handle = NL.productKey({ handle: this.dataset.handle, productId: this.dataset.productId });
      if (!this.handle) { this.hidden = true; return; }
      this.sync();
      if (this._bound) return;
      this._bound = true;
      this.button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const added = NL.wishlist.toggle(this.handle);
        this.sync();
        if (added && !reduced()) {
          this.button.animate(
            [{ transform: 'scale(1)' }, { transform: 'scale(1.22)' }, { transform: 'scale(0.94)' }, { transform: 'scale(1)' }],
            { duration: 460, easing: 'cubic-bezier(.34,1.4,.64,1)' }
          );
        }
        NL.toast(added ? strings.wishlistAdded : strings.wishlistRemoved, {
          variant: 'info',
          action: added && routes.wishlist ? { href: routes.wishlist, label: strings.wishlistView } : null,
          timeout: 2600,
        });
      });
      this.onChange = (event) => {
        const handle = event.detail && event.detail.handle;
        if (!handle || handle === this.handle) this.sync();
      };
      document.addEventListener('wishlist:changed', this.onChange);
    }

    disconnectedCallback() {
      if (this.onChange) document.removeEventListener('wishlist:changed', this.onChange);
      this._bound = false;
    }

    sync() {
      const active = NL.wishlist.has(this.handle);
      this.button.setAttribute('aria-pressed', String(active));
      this.toggleAttribute('data-active', active);
      const title = this.dataset.title ? ` ${this.dataset.title}` : '';
      this.button.setAttribute(
        'aria-label',
        `${active ? strings.wishlistRemove : strings.wishlistAdd}${title ? ':' + title : ''}`
      );
    }
  }

  define('wishlist-toggle', WishlistToggle);

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.onChange = () => this.render();
      document.addEventListener('wishlist:changed', this.onChange);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:changed', this.onChange);
    }

    render() {
      const count = NL.wishlist.count();
      this.dataset.count = String(count);
      this.textContent = String(count);
      this.hidden = count === 0;
    }
  }

  define('wishlist-count', WishlistCount);

  /* ==========================================================================
     <menu-drawer>: full screen mobile navigation (extends nl-dialog)
     ========================================================================== */

  class MenuDrawer extends NLDialog {
    connectedCallback() {
      super.connectedCallback();
      if (this._menuBound) return;
      this._menuBound = true;
      this.addEventListener('click', (event) => {
        const link = event.target.closest('a[href]');
        if (link && !link.hasAttribute('data-keep-open')) this.close(true);
      });
      const desktop = window.matchMedia('(min-width: 1280px)');
      desktop.addEventListener('change', (mq) => {
        if (mq.matches) this.close(true);
      });
    }
  }

  define('menu-drawer', MenuDrawer);

  /* ==========================================================================
     <header-menu>: desktop dropdown / mega menu with hover intent
     Markup: li[data-mega-item] > a + button[data-mega-toggle][aria-controls] + [data-mega-panel]
     li[data-menu-dropdown] (snippets/nl-nav-group.liquid): small dropdown, no solid band or scrim on the header;
     ArrowDown / ArrowUp on the chevron open it on the first / last link, arrows, Home and End move inside it.
     ========================================================================== */

  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.header = this.closest('[data-nl-header]');
      this.items = $$('[data-mega-item]', this);
      const fine = window.matchMedia('(hover: hover) and (pointer: fine)');

      this.items.forEach((item) => {
        const toggle = item.querySelector('[data-mega-toggle]');
        const panel = item.querySelector('[data-mega-panel]');
        if (!toggle || !panel) return;
        panel.inert = true;
        let openTimer;
        let closeTimer;

        item.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse' || !fine.matches) return;
          window.clearTimeout(closeTimer);
          openTimer = window.setTimeout(() => this.open(item), this.anyOpen() ? 0 : 90);
        });
        item.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          window.clearTimeout(openTimer);
          closeTimer = window.setTimeout(() => this.close(item), 200);
        });
        toggle.addEventListener('click', (event) => {
          event.preventDefault();
          if (item.hasAttribute('data-open')) this.close(item);
          else this.open(item);
        });
        item.addEventListener('focusout', (event) => {
          if (event.relatedTarget && !item.contains(event.relatedTarget)) this.close(item);
        });
        item.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || !item.hasAttribute('data-open')) return;
          event.stopPropagation();
          this.close(item);
          toggle.focus();
        });
        if (item.hasAttribute('data-menu-dropdown')) this.bindDropdownKeys(item, toggle, panel);
      });

      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.closeAll();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeAll();
      });
    }

    bindDropdownKeys(item, toggle, panel) {
      const links = () => $$('a[href]', panel);
      const focusAt = (index) => {
        const list = links();
        if (!list.length) return;
        list[(index + list.length) % list.length].focus();
      };
      toggle.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        event.preventDefault();
        this.open(item);
        focusAt(event.key === 'ArrowDown' ? 0 : -1);
      });
      panel.addEventListener('keydown', (event) => {
        const list = links();
        const at = list.indexOf(document.activeElement);
        if (at < 0) return;
        if (event.key === 'ArrowDown') focusAt(at + 1);
        else if (event.key === 'ArrowUp') focusAt(at - 1);
        else if (event.key === 'Home') focusAt(0);
        else if (event.key === 'End') focusAt(-1);
        else return;
        event.preventDefault();
      });
    }

    anyOpen() {
      return this.items.some((item) => item.hasAttribute('data-open'));
    }

    anyMegaOpen() {
      return this.items.some((item) => item.hasAttribute('data-open') && !item.hasAttribute('data-menu-dropdown'));
    }

    open(item) {
      this.items.forEach((other) => other !== item && this.close(other));
      item.setAttribute('data-open', '');
      const toggle = item.querySelector('[data-mega-toggle]');
      const panel = item.querySelector('[data-mega-panel]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      if (panel) panel.inert = false;
      if (this.header && !item.hasAttribute('data-menu-dropdown')) this.header.setAttribute('data-mega-open', '');
    }

    close(item) {
      if (!item.hasAttribute('data-open')) return;
      item.removeAttribute('data-open');
      const toggle = item.querySelector('[data-mega-toggle]');
      const panel = item.querySelector('[data-mega-panel]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (panel) panel.inert = true;
      if (this.header && !this.anyMegaOpen()) this.header.removeAttribute('data-mega-open');
    }

    closeAll() {
      this.items.forEach((item) => this.close(item));
    }
  }

  define('header-menu', HeaderMenu);

  /* ==========================================================================
     <accordion-item [data-group="name"]><details><summary>..</summary><div data-accordion-content>..</div></details>
     Smooth height animation with the Web Animations API.
     ========================================================================== */

  class AccordionItem extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      if (!this.details || this._bound) return;
      this.summary = this.details.querySelector(':scope > summary');
      this.content =
        this.details.querySelector(':scope > [data-accordion-content]') ||
        (this.summary && this.summary.nextElementSibling);
      if (!this.summary || !this.content) return;
      this._bound = true;
      this.summary.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggle();
      });
    }

    toggle(force) {
      const shouldOpen = typeof force === 'boolean' ? force : !this.details.open || this.details.classList.contains('is-closing');
      if (shouldOpen) this.open();
      else this.close();
    }

    open() {
      if (this.details.open && !this.details.classList.contains('is-closing')) return;
      const group = this.dataset.group;
      if (group) {
        $$(`accordion-item[data-group="${CSS.escape(group)}"]`).forEach((other) => {
          if (other !== this && typeof other.close === 'function') other.close();
        });
      }
      const from = this.details.open ? this.content.offsetHeight : 0;
      this.details.classList.remove('is-closing');
      this.details.open = true;
      emit('accordion:toggle', { open: true }, this);
      if (reduced()) return;
      this.run(from, this.content.scrollHeight);
    }

    close() {
      if (!this.details.open || this.details.classList.contains('is-closing')) return;
      emit('accordion:toggle', { open: false }, this);
      if (reduced()) {
        this.details.open = false;
        return;
      }
      this.details.classList.add('is-closing');
      this.run(this.content.offsetHeight, 0, () => {
        this.details.open = false;
        this.details.classList.remove('is-closing');
      });
    }

    run(from, to, done) {
      if (this.animation) this.animation.cancel();
      this.content.style.overflow = 'hidden';
      const duration = Math.min(520, 240 + Math.abs(to - from) * 0.35);
      this.animation = this.content.animate(
        [
          { height: `${from}px`, opacity: from ? 1 : 0 },
          { height: `${to}px`, opacity: to ? 1 : 0 },
        ],
        { duration, easing: 'cubic-bezier(.2,.7,.2,1)' }
      );
      this.animation.onfinish = () => {
        this.animation = null;
        this.content.style.overflow = '';
        if (done) done();
      };
      this.animation.oncancel = () => {
        this.animation = null;
      };
    }
  }

  define('accordion-item', AccordionItem);

  /* ==========================================================================
     <product-form data-section-id data-product-url data-product-id [data-update-url]>
     - intercepts submit and adds through NL.cart.addForm (works for any form 'product')
     - variant selection: inputs with [data-option-index="0..2"] (radios or selects) + script[data-variants] JSON
     - after a variant change, regions [data-variant-region="name"] inside the section are re-rendered
     Button: [type=submit] with a child [data-add-label]. Error box: [data-form-error] (optional).
     ========================================================================== */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || this._bound) return;
      this._bound = true;
      this.submit = this.form.querySelector('[type="submit"]');
      this.errorBox = this.querySelector('[data-form-error]');
      this.variants = this.readVariants();
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('change', (event) => {
        if (event.target.closest('[data-option-index]')) this.onOptionChange();
      });
    }

    readVariants() {
      const script = this.querySelector('script[data-variants]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        return null;
      }
    }

    get labelEl() {
      return this.submit && (this.submit.querySelector('[data-add-label]') || null);
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.submit || this.submit.disabled || this.submit.getAttribute('aria-disabled') === 'true') return;
      this.setError('');
      this.submit.setAttribute('aria-busy', 'true');
      try {
        await NL.cart.addForm(this.form, { source: 'product-form', opener: this.submit });
        this.flashAdded();
      } catch (error) {
        this.setError(error.message || strings.cartError);
      } finally {
        this.submit.removeAttribute('aria-busy');
      }
    }

    flashAdded() {
      const label = this.labelEl;
      if (!label) return;
      const original = label.dataset.original || label.textContent;
      label.dataset.original = original;
      if (this.submit.closest('.nl-v5-routine__form')) {
        const originalAria = this.submit.dataset.originalAriaLabel || this.submit.getAttribute('aria-label');
        this.submit.dataset.originalAriaLabel = originalAria;
        label.textContent = '✓';
        const productTitle = (this.submit.dataset.productTitle || '').trim();
        this.submit.setAttribute('aria-label', productTitle ? fill(strings.cartAddedNamed, { title: productTitle }) : strings.cartAdded);
        this.submit.classList.add('is-added');
        window.clearTimeout(this.flashTimer);
        this.flashTimer = window.setTimeout(() => {
          label.textContent = original;
          this.submit.setAttribute('aria-label', originalAria);
          this.submit.classList.remove('is-added');
        }, 1800);
        return;
      }
      label.textContent = strings.added || original;
      this.submit.classList.add('is-added');
      window.clearTimeout(this.flashTimer);
      this.flashTimer = window.setTimeout(() => {
        label.textContent = original;
        this.submit.classList.remove('is-added');
      }, 1800);
    }

    setError(message) {
      if (!this.errorBox) {
        if (message) NL.toast(message, { variant: 'error' });
        return;
      }
      const text = this.errorBox.querySelector('[data-form-error-text]') || this.errorBox;
      text.textContent = message;
      this.errorBox.hidden = !message;
    }

    selectedOptions() {
      const values = [];
      $$('[data-option-index]', this).forEach((el) => {
        const index = Number(el.dataset.optionIndex);
        if (el.matches('select')) values[index] = el.value;
        else if (el.matches('input[type="radio"]') && el.checked) values[index] = el.value;
        else if (el.matches('input:not([type="radio"])')) values[index] = el.value;
      });
      return values;
    }

    onOptionChange() {
      if (!this.variants) return;
      const selected = this.selectedOptions();
      const variant =
        this.variants.find((v) => (v.options || []).every((option, i) => selected[i] === undefined || option === selected[i])) ||
        null;
      this.applyVariant(variant);
    }

    applyVariant(variant) {
      const idInput = this.form.querySelector('[name="id"]');
      if (variant && idInput) idInput.value = variant.id;

      if (this.submit) {
        const label = this.labelEl;
        if (!variant) {
          this.submit.disabled = true;
          if (label) label.textContent = strings.unavailable;
        } else if (!variant.available) {
          this.submit.disabled = true;
          if (label) label.textContent = strings.soldOut;
        } else {
          this.submit.disabled = false;
          if (label) label.textContent = label.dataset.original || strings.addToCart;
        }
      }

      if (variant && this.hasAttribute('data-update-url')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      }

      emit('variant:change', {
        variant,
        productId: this.dataset.productId,
        sectionId: this.dataset.sectionId,
        form: this,
      });

      if (variant && this.dataset.sectionId && this.dataset.productUrl) this.renderRegions(variant.id);
    }

    async renderRegions(variantId) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const sectionId = this.dataset.sectionId;
      const scope = document.getElementById(`shopify-section-${sectionId}`) || document;
      const regions = $$('[data-variant-region]', scope);
      if (!regions.length) return;
      try {
        const url = `${this.dataset.productUrl}${this.dataset.productUrl.includes('?') ? '&' : '?'}variant=${variantId}&section_id=${sectionId}`;
        const response = await fetch(url, { signal: this.controller.signal, credentials: 'same-origin' });
        const doc = parseHTML(await response.text());
        regions.forEach((region) => {
          const fresh = doc.querySelector(`[data-variant-region="${CSS.escape(region.dataset.variantRegion)}"]`);
          if (fresh) region.innerHTML = fresh.innerHTML;
        });
      } catch (error) {
        /* aborted or offline: keep the current markup */
      }
    }
  }

  define('product-form', ProductForm);

  /* ==========================================================================
     <sticky-atc data-target="#MainAtc" [data-form="#ProductForm-id"]>
     Shows when the main add to cart leaves the viewport upwards, hides over the footer.
     A [data-sticky-submit] button submits the main product form.
     ========================================================================== */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.target = this.dataset.target ? document.querySelector(this.dataset.target) : null;
      this.panel = this.firstElementChild;
      if (!this.target || !('IntersectionObserver' in window)) return;
      this._bound = true;
      this.past = false;
      this.overFooter = false;
      this.update();

      this.observer = new IntersectionObserver(([entry]) => {
        this.past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.update();
      });
      this.observer.observe(this.target);

      const footer = document.querySelector('.nl-footer');
      if (footer) {
        this.footerObserver = new IntersectionObserver(([entry]) => {
          this.overFooter = entry.isIntersecting;
          this.update();
        });
        this.footerObserver.observe(footer);
      }

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-sticky-submit]');
        if (!button) return;
        event.preventDefault();
        const form = this.dataset.form ? document.querySelector(this.dataset.form) : this.target.closest('form');
        if (!form) return;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.footerObserver) this.footerObserver.disconnect();
      this._bound = false;
    }

    update() {
      const visible = this.past && !this.overFooter;
      this.toggleAttribute('data-visible', visible);
      if (this.panel) this.panel.inert = !visible;
      document.documentElement.toggleAttribute('data-sticky-atc', visible);
    }
  }

  define('sticky-atc', StickyAtc);

  /* ==========================================================================
     <media-gallery data-section-id>
       [data-gallery-track] > [data-gallery-slide][data-media-id]  (scroll snap, swipe on touch)
       [data-gallery-thumb] buttons (desktop), [data-gallery-dots] container (mobile, built here)
       [data-gallery-zoom][data-zoom-src] triggers, <nl-dialog data-gallery-zoom-dialog> with img[data-zoom-image]
     ========================================================================== */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track || this._bound) return;
      this._bound = true;
      this.slides = $$('[data-gallery-slide]', this);
      this.thumbs = $$('[data-gallery-thumb]', this);
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.hasAttribute('data-active')));
      this.buildDots();

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.6) this.setActive(this.slides.indexOf(entry.target));
            });
          },
          { root: this.track, threshold: [0.6] }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));
      }

      this.thumbs.forEach((thumb, i) =>
        thumb.addEventListener('click', (event) => {
          event.preventDefault();
          this.goTo(i);
        })
      );

      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1);
        }
      });

      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-gallery-zoom]');
        if (!trigger) return;
        event.preventDefault();
        this.openZoom(trigger);
      });

      this.onVariant = (event) => {
        const { variant, sectionId } = event.detail || {};
        if (!variant || !variant.featured_media) return;
        if (sectionId && this.dataset.sectionId && sectionId !== this.dataset.sectionId) return;
        this.goToMedia(variant.featured_media.id);
      };
      document.addEventListener('variant:change', this.onVariant);
      this.setActive(this.index);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onVariant) document.removeEventListener('variant:change', this.onVariant);
      this._bound = false;
    }

    buildDots() {
      const holder = this.querySelector('[data-gallery-dots]');
      if (!holder) return;
      if (this.slides.length < 2) {
        holder.hidden = true;
        return;
      }
      holder.innerHTML = '';
      this.dots = this.slides.map((slide, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'nl-gallery__dot';
        dot.setAttribute('aria-label', fill(strings.slideOf, { index: i + 1, count: this.slides.length }));
        dot.addEventListener('click', () => this.goTo(i));
        holder.append(dot);
        return dot;
      });
    }

    goTo(index) {
      if (!this.slides.length) return;
      const i = Math.max(0, Math.min(this.slides.length - 1, index));
      const slide = this.slides[i];
      this.track.scrollTo({ left: slide.offsetLeft, behavior: reduced() ? 'auto' : 'smooth' });
      this.setActive(i);
    }

    goToMedia(mediaId) {
      const i = this.slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      if (i >= 0) this.goTo(i);
    }

    setActive(index) {
      if (index < 0) return;
      const changed = index !== this.index;
      this.index = index;
      this.slides.forEach((slide, i) => slide.toggleAttribute('data-active', i === index));
      this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
      (this.dots || []).forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
      if (changed) emit('gallery:change', { index, mediaId: this.slides[index] && this.slides[index].dataset.mediaId }, this);
    }

    openZoom(trigger) {
      const host = this.querySelector('[data-gallery-zoom-dialog]');
      if (!host || typeof host.open !== 'function') return;
      const image = host.querySelector('[data-zoom-image]');
      if (image && trigger.dataset.zoomSrc) {
        image.src = trigger.dataset.zoomSrc;
        if (trigger.dataset.zoomSrcset) image.srcset = trigger.dataset.zoomSrcset;
        image.alt = trigger.dataset.zoomAlt || '';
      }
      host.open(trigger);
    }
  }

  define('media-gallery', MediaGallery);

  /* ==========================================================================
     <scroll-rail class="nl-rail" [data-fade]>
       [data-rail-track] (direct children are items), [data-rail-prev], [data-rail-next]
     Host attributes: data-at-start, data-at-end, data-no-overflow. Mouse drag to scroll.
     ========================================================================== */

  class ScrollRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail-track]');
      if (!this.track || this._bound) return;
      this._bound = true;
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      if (this.prev) this.prev.addEventListener('click', () => this.page(-1));
      if (this.next) this.next.addEventListener('click', () => this.page(1));
      this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.queueUpdate());
        this.resizeObserver.observe(this.track);
      }
      this.bindDrag();
      // Next frame, batched with the rest of the boot (a synchronous read here forced a layout per rail)
      this.queueUpdate();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this._bound = false;
    }

    queueUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    get step() {
      const item = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      if (!item) return this.track.clientWidth;
      const width = item.getBoundingClientRect().width + gap;
      const perPage = Math.max(1, Math.floor((this.track.clientWidth + gap) / width));
      return width * perPage;
    }

    page(direction) {
      this.track.scrollBy({ left: direction * this.step, behavior: reduced() ? 'auto' : 'smooth' });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = Math.abs(this.track.scrollLeft);
      const noOverflow = max <= 2;
      const atStart = x <= 2;
      const atEnd = x >= max - 2;
      this.toggleAttribute('data-no-overflow', noOverflow);
      this.toggleAttribute('data-at-start', atStart);
      this.toggleAttribute('data-at-end', atEnd);
      if (this.prev) {
        this.prev.disabled = atStart || noOverflow;
        this.prev.setAttribute('aria-disabled', String(this.prev.disabled));
      }
      if (this.next) {
        this.next.disabled = atEnd || noOverflow;
        this.next.setAttribute('aria-disabled', String(this.next.disabled));
      }
    }

    bindDrag() {
      let startX = 0;
      let startScroll = 0;
      let moved = false;
      let active = false;

      const onMove = (event) => {
        if (!active) return;
        const dx = event.clientX - startX;
        if (!moved && Math.abs(dx) > 5) {
          moved = true;
          this.classList.add('is-dragging');
        }
        if (moved) this.track.scrollLeft = startScroll - dx;
      };
      const onUp = () => {
        if (!active) return;
        active = false;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        if (moved) {
          const swallow = (event) => {
            event.preventDefault();
            event.stopPropagation();
          };
          this.track.addEventListener('click', swallow, { capture: true, once: true });
          window.setTimeout(() => this.track.removeEventListener('click', swallow, { capture: true }), 50);
          this.classList.remove('is-dragging');
        }
      };

      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (event.target.closest('input, select, textarea, button, [data-no-drag]')) return;
        active = true;
        moved = false;
        startX = event.clientX;
        startScroll = this.track.scrollLeft;
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      });

      this.track.addEventListener('dragstart', (event) => {
        if (event.target.closest('a, img')) event.preventDefault();
      });
    }
  }

  define('scroll-rail', ScrollRail);

  /* ==========================================================================
     <tabs-component>: [role=tablist] > [role=tab][aria-controls], [role=tabpanel]
     ========================================================================== */

  class TabsComponent extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.tabs = $$('[role="tab"]', this).filter((tab) => tab.closest('tabs-component') === this);
      if (!this.tabs.length) return;
      this._bound = true;
      this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i, true));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, i));
      });
      const initial = Math.max(
        0,
        this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
      );
      this.select(initial, false);
    }

    select(index, focus) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        const panel = this.panels[i];
        if (panel) panel.hidden = !selected;
      });
      if (focus) this.tabs[index].focus();
      const panel = this.panels[index];
      if (panel && focus && !reduced()) {
        panel.animate([{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }], {
          duration: 320,
          easing: 'cubic-bezier(.2,.7,.2,1)',
        });
      }
      emit('tabs:change', { index, tab: this.tabs[index], panel }, this);
    }

    onKeydown(event, index) {
      const last = this.tabs.length - 1;
      const keys = {
        ArrowRight: index === last ? 0 : index + 1,
        ArrowDown: index === last ? 0 : index + 1,
        ArrowLeft: index === 0 ? last : index - 1,
        ArrowUp: index === 0 ? last : index - 1,
        Home: 0,
        End: last,
      };
      if (!(event.key in keys)) return;
      event.preventDefault();
      this.select(keys[event.key], true);
    }
  }

  define('tabs-component', TabsComponent);

  /* ==========================================================================
     Misc: cookie preferences link (Shopify privacy banner), external links
     ========================================================================== */

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cookie-preferences]');
    if (!trigger) return;
    const banner = window.privacyBanner;
    if (banner && typeof banner.showPreferences === 'function') {
      event.preventDefault();
      banner.showPreferences();
    }
  });
})();
